<template>
  <div class="doc-content">
    <div class="app-header">
      <h1>Baby-Veranstaltungen in Koblenz: Termine & Events</h1>
      <p>Ob Baby-Flohmarkt, Eltern-Kind-Café oder Infoabend: Entdeckt aktuelle Events und einmalige Termine für Familien in Koblenz und der Region.</p>
    </div>

    <div class="search-wrapper">
      <input
        type="search"
        v-model="searchQuery"
        placeholder="Veranstaltungen durchsuchen…"
      >
    </div>

    <div v-if="loading" class="info-message loading">
      Daten werden geladen...
    </div>
    
    <div v-else-if="error" class="info-message error">
      Fehler beim Laden der Daten: {{ error }}
    </div>
    
    <div v-else>
      <p v-if="filteredEvents.length === 0" class="no-events">
        Keine passenden Veranstaltungen gefunden.
      </p>
      
      <div
        v-for="(event, index) in filteredEvents"
        :key="event.title + event.start_date"
      >
        <!-- Monats-Trenner -->
        <div
          v-if="isNewMonth(event, index)"
          class="month-separator"
          @click="toggleMonth(getMonthKey(event.start_date))"
        >
          <span class="month-title">
             {{ formatMonth(event.start_date) }}
          </span>
          <span class="month-meta">
            <span class="count">
              ({{ eventsPerMonth[getMonthKey(event.start_date)] }}) 
            </span>
            <span class="icon">
              {{ collapsedMonths[getMonthKey(event.start_date)] ? '▶' : '▼' }}
            </span>
          </span>
        </div>
        
        <section
          v-if="!collapsedMonths[getMonthKey(event.start_date)]"
          class="event-card"
        >
          <article>
            <h2>
              {{ event.title }}
              <span
                v-if="getDayBadge(event.start_date)"
                :class="['day-badge', getDayBadge(event.start_date)]"
              >
                {{ getDayBadge(event.start_date) === 'today' ? 'Heute' : 'Morgen' }}
              </span>
            </h2>
          
            <p class="date">
              📅 {{ formatDate(event.start_date) }}
              <span v-if="event.start_time"> · {{ event.start_time }}<span v-if="event.end_time"> - {{ event.end_time }}</span> Uhr</span>
              
            </p>
          
            <p v-if="event.location_name">
              📍 {{ event.location_name }}<span v-if="event.location_address"> · {{ event.location_address }}</span>
            </p>
          
            <p v-if="event.organizer">
              👤 {{ event.organizer }}
            </p>
          
            <p v-if="event.price >= 0">
              💶 {{ formatPrice(event.price) }}
            </p>

          
            <p v-if="event.description">
              {{ event.description }}
            </p>
          
            <a v-if="event.url" :href="event.url" target="_blank">
              Mehr Infos
            </a>
          </article>
        </section> 
      </div>
    </div>

    <hr class="form-separator">
    <div class="table-wrapper">
      <section
        ref="addEventSection"
        class="add-content-section"
      >
        <h2>➕ Veranstaltung hinzufügen</h2>
        
        <form @submit.prevent="submitEvent" class="content-form">
        
          <div class="form-group">
            <label>Titel der Veranstaltung *</label>
            <input type="text" v-model="form.title" required>
          </div>
        
          <div class="form-group">
            <label>Beschreibung</label>
            <textarea v-model="form.description"></textarea>
          </div>
        
          <div class="form-group">
            <label>Datum *</label>
            <input type="date" v-model="form.start_date" required>
          </div>
        
          <div class="form-group">
            <label>Startzeit</label>
            <input type="time" v-model="form.start_time" step="60" @change="normalizeTime('start_time')">
          </div>
        
          <div class="form-group">
            <label>Endzeit</label>
            <input type="time" v-model="form.end_time" step="60" @change="normalizeTime('end_time')" :disabled="!form.start_time">
          </div>
        
          <div class="form-group">
            <label>Ort *</label>
            <input type="text" v-model="form.location_name" required>
          </div>
        
          <div class="form-group">
            <label>Adresse</label>
            <input type="text" v-model="form.location_address">
          </div>
        
          <div class="form-group">
            <label>Veranstalter</label>
            <input type="text" v-model="form.organizer">
          </div>
        
          <div class="form-group">
            <label>Kosten (in €)</label>
            <div style="display: flex; gap: 10px; align-items: center;">
              <input 
                type="number" 
                min="0" 
                step="any" 
                v-model.number="form.price"
                :disabled="form.price === -1"
                :required="form.price !== -1"
              >
              <label>
                <input 
                  type="checkbox" 
                  :checked="form.price === -1"
                  @change="$event.target.checked ? form.price = -1 : form.price = null"
                > Unbekannt
              </label>
            </div>
          </div>
        
          <div class="form-group">
            <label>Link</label>
            <input type="url" v-model="form.url">
          </div>
        
          <!--
          <div class="form-group">
            <label>Kategorie</label>
            <input type="text" v-model="form.category">
          </div>
          -->
        
          <!-- Honeypot -->
          <div class="honeypot-field">
            <input type="text" v-model="form.honeypot">
          </div>
        
          <button type="submit" class="cta-button" :disabled="isSubmitting">
            {{ isSubmitting ? 'Wird gesendet…' : 'Veranstaltung eintragen' }}
          </button>
        
          <div v-if="submitStatus" :class="['status-message', submitStatus.success ? 'success' : 'error']">
            {{ submitStatus.message }}
          </div>
        </form>
      </section>
    </div>
    
    <hr class="form-separator">
    <div class="disclaimer">
      <h3>Hinweis zur Liste</h3>
      <p>
          Diese Veranstaltungsliste ist ein Service für die WhatsApp-Community "Koblenz mit Baby"
          und wird aktiv von Euch, den Community-Mitgliedern, gepflegt und ergänzt.
      </p>
      <p>
          Ich übernehme keine Gewähr für die Aktualität, Vollständigkeit,
          Richtigkeit oder inhaltliche Zulässigkeit der hier eingetragenen Angebote, da diese von Dritten stammen. 
          Die Verantwortung für die Inhalte der jeweiligen Veranstaltung liegt bei dem Community-Mitglied, das sie eingetragen hat.
      </p>
      <p>
          Sollten Euch Fehler, unvollständige Angaben oder rechtswidrige Inhalte auffallen, bitte ich Euch dringend um einen
          umgehenden Hinweis an <router-link @click.native="closeMenu" to="/kontakt/">mich</router-link>.
      </p>
    </div>
    
    <button
      class="floating-action-btn"
      @click="handleFloatingButton"
      :aria-label="showScrollTop ? 'Nach oben' : 'Neue Veranstaltung hinzufügen'"
    >
      <span v-if="showScrollTop">⬆️</span>
      <span v-else>➕</span>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
// 1. Statische Daten importieren (werden beim Build von GitHub erstellt)
import staticEvents from '@/data/events-static.json';

const SECRET_KEY = 'MeinGanzGeheimerSchluesselFuerKO';
const API_URL = 'https://script.google.com/macros/s/AKfycbxEK3USwJ-o5NJiGTBbvd0-2Pw7rYZ9OR0Xp4BJ7a62g-r8Ip7-WtynfxkCx6uhY0c4/exec';
const events = ref(staticEvents || []);
const loading = ref(false);
const error = ref(null);
const addEventSection = ref(null);
const showScrollTop = ref(false);

const fetchData = async () => {
  try {
    const response = await fetch(API_URL, { redirect: 'follow' });
    
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Zu viele Anfragen. Bitte später erneut versuchen.');
      } else if (response.status === 503) {
        throw new Error('Dienst vorübergehend nicht verfügbar.');
      } else {
        throw new Error(`HTTP-Fehler! Status: ${response.status}`);
      }
    }
    
    const data = await response.json();
    // 2. Live-Update: Überschreibe statische Daten mit frischen API-Daten
    events.value = data || [];
    initCollapsedMonths();
    // Strukturierte Daten für Google aktualisieren
    injectStructuredData(data);
  } catch (err) {
    console.error("API Fehler, nutze statische Daten:", err);
    if (err.message.includes('Failed to fetch')) {
      error.value = 'Keine Internetverbindung. Bitte überprüfen Sie Ihre Verbindung.';
    } else {
      error.value = err.message || 'Fehler beim Laden der Daten.';
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  // SEO: Strukturierte Daten sofort aus statischen Daten bauen
  if (events.value.length > 0) {
    injectStructuredData(events.value);
  }
  fetchData(); // Dann Live-Update
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
});

const formatDate = (date) =>
  new Date(date).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  
const formatPrice = (price) => {
  if (price === -1 || price === "-1") return "unbekannt";
  if (price === 0 || price === "0") return "kostenlos";
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};  
  
const normalizeTime = (field) => {
  if (!form.value[field]) return;

  // Falls Browser HH:mm:ss liefert → Sekunden abschneiden
  const parts = form.value[field].split(':');
  form.value[field] = parts[0] + ':' + parts[1];
};

const isNewMonth = (event, index) => {
  if (index === 0) return true;

  const current = new Date(event.start_date);
  const previous = new Date(events.value[index - 1].start_date);

  return (
    current.getMonth() !== previous.getMonth() ||
    current.getFullYear() !== previous.getFullYear()
  );
};

const formatMonth = (date) =>
  new Date(date).toLocaleDateString('de-DE', {
    month: 'long',
    year: 'numeric'
  });
  
const injectStructuredData = (events) => {
  const scripts = events.map(event => ({
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "startDate": event.start_date + (event.start_time ? 'T' + event.start_time : ''),
    "location": {
      "@type": "Place",
      "name": event.location_name,
      "address": event.location_address
    },
    "description": event.description,
    "organizer": {
      "@type": "Organization",
      "name": event.organizer
    },
    "offers": {
      "@type": "Offer",
      "price": event.price || "0",
      "priceCurrency": "EUR",
      "url": event.url
    }
  }));

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(scripts);
  document.head.appendChild(script);
};



const isSubmitting = ref(false);
const submitStatus = ref(null);

const form = ref({
  title: '',
  description: '',
  start_date: '',
  start_time: '',
  end_time: '',
  location_name: '',
  location_address: '',
  organizer: '',
  price: '',
  url: '',
  category: '',
  honeypot: ''
});

const submitEvent = async () => {
  isSubmitting.value = true;
  submitStatus.value = null;

  if (form.value.honeypot) {
    submitStatus.value = { success: false, message: 'Bot erkannt.' };
    isSubmitting.value = false;
    return;
  }

  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({
        key: SECRET_KEY,
        type: 'event',
        data: form.value
      })
    });

    const result = await res.json();

    if (result.success) {
      submitStatus.value = { success: true, message: '✅ Veranstaltung gespeichert!' };
      setTimeout(() => window.location.reload(), 2000);
    } else {
      submitStatus.value = { success: false, message: result.message };
    }

  } catch (e) {
    submitStatus.value = { success: false, message: 'Serverfehler.' };
  } finally {
    isSubmitting.value = false;
  }
};

const onScroll = () => {
  if (!addEventSection.value) return;

  const rect = addEventSection.value.getBoundingClientRect();

  // Formular sichtbar → Button wird "nach oben"
  showScrollTop.value = rect.top < window.innerHeight * 0.6;
};

const handleFloatingButton = () => {
  if (showScrollTop.value) {
    // Nach oben
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    // Zum Formular
    addEventSection.value.scrollIntoView({ behavior: 'smooth' });
  }
};


//
// Monate einklappen
//
const collapsedMonths = ref({}); // { '2026-02': true/false }

const getMonthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const toggleMonth = (monthKey) => {
  collapsedMonths.value[monthKey] = !collapsedMonths.value[monthKey];
};

const eventsPerMonth = computed(() => {
  const map = {};
  events.value.forEach(event => {
    const key = getMonthKey(event.start_date);
    if (!map[key]) map[key] = 0;
    map[key]++;
  });
  return map;
});


//
// Default handling zum Einklappen
//
const MIN_VISIBLE_EVENTS = 5;

const initCollapsedMonths = () => {
  const monthEventCount = {};
  const orderedMonths = [];

  // Events sind bereits chronologisch sortiert
  events.value.forEach(event => {
    const key = getMonthKey(event.start_date);

    if (!monthEventCount[key]) {
      monthEventCount[key] = 0;
      orderedMonths.push(key);
    }
    monthEventCount[key]++;
  });

  let visibleCount = 0;
  const state = {};

  for (const month of orderedMonths) {
    if (visibleCount < MIN_VISIBLE_EVENTS) {
      state[month] = false; // aufgeklappt
      visibleCount += monthEventCount[month];
    } else {
      state[month] = true; // eingeklappt
    }
  }

  collapsedMonths.value = state;
};


//
// Heute / Morgen Badge
//
const getDayBadge = (date) => {
  const eventDate = new Date(date);
  const today = new Date();
  const tomorrow = new Date();
  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(today.getDate() + 1);
  if (eventDate.getTime() === today.getTime()) return 'today';
  if (eventDate.getTime() === tomorrow.getTime()) return 'tomorrow';
  return null;
};


// 
// Suchfunktion
//
const searchQuery = ref('');

const filteredEvents = computed(() => {
  if (!searchQuery.value) return events.value;

  const q = searchQuery.value.toLowerCase();

  return events.value.filter(e =>
    e.title?.toLowerCase().includes(q) ||
    e.description?.toLowerCase().includes(q) ||
    e.location_name?.toLowerCase().includes(q) || 
e.location_address?.toLowerCase().includes(q) || 
    e.organizer?.toLowerCase().includes(q) || 
e.category?.toLowerCase().includes(q) || 
e.url?.toLowerCase().includes(q) 
  );
});
watch(searchQuery, (q) => {
  if (!q) return;

  const openMonths = {};

  filteredEvents.value.forEach(event => {
    openMonths[getMonthKey(event.start_date)] = false;
  });

  collapsedMonths.value = {
    ...collapsedMonths.value,
    ...openMonths
  };
});
</script>

<style scoped>
.doc-content {
  max-width: none;
  margin: 0 auto;
  padding: 10px; /* Reduzierter Padding für Mobile */
  box-sizing: border-box;
}

@media (min-width: 600px) {
  .doc-content {
    padding: 20px;
  }
}

.table-wrapper {
  width: 100%;
  overflow-x: auto; /* Schlüssel, damit dieser Bereich scrollt */
  margin-top: 15px;
}

.month-separator {
  margin: 30px 0 15px;
  padding-bottom: 6px;
  font-size: 1.4em;
  font-weight: bold;
  color: var(--font-color);
  border-bottom: 2px solid rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.month-separator:hover {
  opacity: 0.85;
}

.month-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9em;
  opacity: 0.8;
}

.add-content-section {
  display: inline-block;
  min-width: 90%;
  padding: 20px;
  background-color: var(--main-color-2);
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.content-form {
  display: grid;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 5px;
  color: var(--font-color);
}

.form-group input, .form-group textarea {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1em;
  box-sizing: border-box; 
  width: 100%; 
}

.cta-button {
  background-color: var(--button-color);
  color: white;
  padding: 14px 20px;
  border: none;
  border-radius: 6px;
  font-size: 1.1em;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.cta-button:hover:not(:disabled) {
  background-color: var(--button-hover);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.2);
}

.cta-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

/* Honeypot verstecken */
.honeypot-field {
  position: absolute;
  left: -9999px;
  visibility: hidden;
}

/* Statusmeldungen */
.status-message {
  padding: 10px;
  border-radius: 6px;
  margin-top: 15px;
  font-weight: bold;
}

/* Statusfarben für Usability beibehalten (Grün/Rot) */
.status-message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.no-events {
    font-style: italic;
    color: #666;
    padding: 10px 0;
}


.floating-action-btn {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background-color: var(--button-color);
  color: white;
  font-size: 1.6em;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.floating-action-btn:hover {
  transform: scale(1.05);
  background-color: var(--button-hover);
}




.day-badge {
  margin-left: 10px;
  padding: 4px 8px;
  font-size: 0.75em;
  border-radius: 12px;
  font-weight: bold;
  vertical-align: middle;
}

.day-badge.today {
  background-color: var(--button-color);
  color: var(--font-color-light);
}

.day-badge.tomorrow {
  background-color: var(--button-color);
  color: var(--font-color-light);
}



.search-wrapper {
  margin: 15px 0 25px;
}

.search-wrapper input {
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 1em;
}
</style>